import React, {
  ReactElement,
  useEffect,
  useState,
  useCallback,
} from 'react';
import {SchemaWithClient} from '../../types/SchemaWithClient';
import {Entity} from '../../types/entities/Entity';
import {
  Button as MUIButton, Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableContainer,
  Typography,
} from '@mui/material';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import {SlidingPaneEdit} from '../Page/SlidingPaneEdit';
import api from '../../services/api';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import toast from 'react-hot-toast';
import TableBody from '@mui/material/TableBody';
import {SPNumberField} from '../Table/fields/SPNumberField';
import {SPSelectField} from '../Table/fields/SPSelectField';
import {enumItems} from '../../schema/enumItems';
import {formatNumber} from "../../reducer/actions/formatNumber";
import {formatPrice} from "../../reducer/actions/formatPrice";
import {RefreshRounded} from "@mui/icons-material";
import {SPSelectWithOutAutocomplete} from "../Table/fields/SPSelectWithOutAutocompleteField";

type Props<T extends Entity> = {
  schema: SchemaWithClient<T>;
};

export const Finance = ({
                          schema,
                          values,
                          fullWidth,
                          onChange,
                        }: {
  schema: any;
  values: any;
  fullWidth: boolean;
  onChange: (value: any, column: string) => void;
}): ReactElement => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(false);
  const [properties, setProperties] = useState<any>(undefined);
  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>({});
  const [mnbRates, setMnbRates] = useState<any>(false);
  const [financeData, setFinanceData] = useState<any>(values?.finances);
  const [loading, setLoading] = useState(false);
  const [baseCurrency, setBaseCurrency] = useState<any>(values.group?.base_currency ? values.group?.base_currency : {
    id: 1,
    name: 'EUR'
  });

  const fetchData = useCallback(() => {
    setLoading(true);
    api.get(`/activity-reservations/${values?.id}`).then((res: any) => {
      setFinanceData(res.finances);
    }).finally(()=>{
      setLoading(false);
    });
  }, []);

  const fetchMnbRates = useCallback(() => {
    const searchParams = new URLSearchParams({
      activity: values?.id,
    });

    api.get(`/mnb-rates/group/${values?.group?.id}`, searchParams).then((res: any) => {
      setMnbRates(res);
    });
  }, []);

  useEffect(() => {
    //fetchData();
    fetchMnbRates();
  }, []);

  const calculateFinal = useCallback(() => {

    const newFinanceData = {...financeData};

    // Final Price Sum in EUR / HUF
    if (financeData?.final_price_currency?.id && values.group?.base_currency?.id) {
      if (financeData?.final_price_currency?.id !== values.group?.base_currency?.id) {

        let price = {...financeData}.final_price;
        if (values.group?.base_currency?.id === 1) {
          price = price / mnbRates.EUR;
        }
        if (values.group?.base_currency?.id === 2) {
          price = price / mnbRates.HUF;
        }

        newFinanceData.final_price_in_base_currency = price;
      } else {
        newFinanceData.final_price_in_base_currency = financeData.final_price;
      }
    }

    setFinanceData(newFinanceData);

  }, [financeData]);

  useEffect(() => {
    calculateFinal();
  }, [financeData.final_price_currency])

  const handleChangeCost = (value: any, type: string) => {
    if (type === 'pp') {
      const newFinalCost = value * (values?.group_size ? values.group_size : 1);
      onChange(newFinalCost, 'final_price');

      const currency = financeData?.original_price_currency?.id ? financeData?.original_price_currency?.id : 1;
      const newProfit = currency === 1 ? (newFinalCost - financeData?.cost.EUR) : (newFinalCost - financeData?.cost.HUF);

      setFinanceData({...financeData, final_price: newFinalCost, final_price_pp: value, profit: {...financeData.profit, [currency===1?'EUR':'HUF']: newProfit}});
    } else {
      const newFinalPPCost = value / (values?.group_size ? values.group_size : 1);

      const currency = financeData?.original_price_currency?.id ? financeData?.original_price_currency?.id : 1;
      const newProfit = currency === 1 ? (value - financeData?.cost.EUR) : (value - financeData?.cost.HUF);

      setFinanceData({...financeData, final_price_pp: newFinalPPCost, final_price: value, profit: {...financeData.profit, [currency===1?'EUR':'HUF']: newProfit}});

      onChange(value, 'final_price');
    }
  };

  useEffect(() => {
    calculateFinal();
  }, [financeData.final_price, financeData.final_price_pp])

  return (
    <>
      <Stack
        sx={{
          mb: '20px',
          width: '100%',
        }}
        gap={2}
        direction={'row'}
        justifyContent={'space-between'}
      >
       <Stack direction={'row'} gap={1}>
         <Stack direction="row" gap={1} alignItems={'center'}>
           <Typography
             sx={{
               width: '200px',
             }}
           >
             Base Currency
           </Typography>
           <SPSelectField
             onChange={() => {
             }}
             schema={enumItems.currency}
             defaultValue={values.group?.base_currency}
             forcedValue={undefined}
             disabled={true}
             isMissing={false}
             accordingToValue={undefined}
           />
         </Stack>

         <Stack direction="row" alignItems={'center'} gap={1}>
           <Typography>
             MNB
           </Typography>

           <Chip
             label={`€ ${mnbRates?.EUR}`}
             variant="outlined"
             /*avatar={<Avatar alt="Natacha" src="/static/images/avatar/1.jpg" />}*/
           />

         </Stack>
       </Stack>

       <Stack direction="row" alignItems={'center'} gap={1}>
         {loading ? (
           <>
             <CircularProgress size={'20px'} />
           </>
         ):(
           <>
             <Tooltip title={'Refresh'} >
               <IconButton
                 onClick={()=>{
                   fetchData();
                 }}
                 color={'primary'}
               >
                 <RefreshRounded />
               </IconButton>
             </Tooltip>
           </>
         )}

        </Stack>

      </Stack>

      <TableContainer
        component={Paper}
        sx={{
          width: '100%',
          opacity: loading ? 0.5 : 1,
          pointerEvents: loading ? 'none' : 'all',
        }}
      >
        <Table aria-label="simple table" size="small">
          <TableHead
            sx={{
              '& .MuiTableCell-root': {
                color: 'inherit',
                border: 'none',
              },
              '& .MuiTableRow-root .MuiTableCell-root.MuiTableCell-head': {
                border: 'none',
                fontSize: '14px',
                p: '10px 15px',
              },
              '& .MuiTableRow-root .MuiTableCell-root .MuiFormControl-root.MuiTextField-root': {
                background: '#f9f9f9',
                boxShadow: 'inset 0px 0px 1px #a7a7a7',
              },
              background: '#256861',
              color: '#fff',
            }}
          >
            <TableRow>
              <TableCell></TableCell>
              <TableCell sx={{textAlign: 'right'}}>PP</TableCell>
              <TableCell sx={{textAlign: 'right'}}>SUM</TableCell>
              <TableCell sx={{textAlign: 'center'}}>CCY</TableCell>
              <TableCell sx={{textAlign: 'right'}}>SUM
                in {values.group?.base_currency ? values.group?.base_currency.name : 'EUR'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& .MuiTableCell-root': {
                p: '5px 10px',
              },
              '& .MuiInputBase-root.MuiOutlinedInput-root': {
                paddingRight: '0px',
              },
            }}
          >
            <TableRow sx={{
              /* background: '#f3f3f3',
               '& .MuiInputBase-root.MuiOutlinedInput-root': {
                 background: '#f3f3f3'
               },*/
            }}>
              <TableCell>Original Price</TableCell>
              <TableCell sx={{textAlign: 'right'}}>
                {formatNumber(financeData?.original_price_pp)}
              </TableCell>
              <TableCell sx={{textAlign: 'right'}}>
                {formatNumber(financeData?.original_price)}
              </TableCell>
              <TableCell>
                {financeData?.original_price_currency?.name}
              </TableCell>
              <TableCell sx={{textAlign: 'right'}}>
                {financeData?.original_price_in_base_currency ? formatNumber(financeData?.original_price_in_base_currency) : ''}
              </TableCell>
            </TableRow>
            <TableRow sx={{
              '& .MuiTableCell-root':{
                fontWeight: 'bold',
              },
               '& .MuiInputBase-root.MuiOutlinedInput-root': {
                 background: '#f9f9f9',
                 boxShadow: 'inset 0px 0px 1px #5b5b5b',

                 '& .MuiInputBase-input':{
                   fontWeight: 'bold',
                 }

               },
            }}>
              <TableCell>
                <b>Final Price</b>
              </TableCell>
              <TableCell>
                <SPNumberField
                  withOutEndAdornment={true}
                  isActive
                  defaultValue={financeData?.final_price_pp}
                  onChange={(value) => {
                    handleChangeCost(value, 'pp');
                  }}
                />
              </TableCell>
              <TableCell>
                <SPNumberField
                  withOutEndAdornment={true}
                  isActive
                  defaultValue={financeData?.final_price}
                  onChange={(value) => {
                    handleChangeCost(value, 'full');
                  }}
                />
              </TableCell>
              <TableCell>
                <SPSelectWithOutAutocomplete
                  onChange={(value) => {
                    //setFinanceData({...financeData, final_price_currency: value})
                    onChange(value, 'final_price_currency');
                  }}
                  schema={enumItems.currency}
                  defaultValue={financeData?.final_price_currency}
                  forcedValue={undefined}
                  disabled={false}
                  isMissing={false}
                  accordingToValue={undefined}
                />
              </TableCell>
              <TableCell sx={{textAlign: 'right'}}>
                {financeData?.final_price_in_base_currency ? formatPrice(financeData?.final_price_in_base_currency, (values.group?.base_currency?.id === 1 ? 2 : 0), true) : ''}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{background: '#f3f3f3'}} colSpan={5}>&nbsp;</TableCell>
            </TableRow>

            {financeData.guide_costs.costs.map((cost: any) => {
              return (
                <TableRow>
                  <TableCell>{cost?.name}</TableCell>
                  <TableCell sx={{textAlign: 'right'}}>
                    {cost?.price_pp ? formatNumber(cost?.price_pp) : ''}
                  </TableCell>
                  <TableCell sx={{textAlign: 'right'}}>
                    {cost?.price ? formatNumber(cost?.price) : ''}
                  </TableCell>
                  <TableCell>
                    {cost?.currency?.name}
                  </TableCell>
                  <TableCell sx={{textAlign: 'right'}}>
                    {cost?.price_in_base_currency ? formatNumber(cost?.price_in_base_currency) : ''}
                  </TableCell>
                </TableRow>
              );
            })}

            {financeData.service_costs.costs.map((cost: any) => {
              return (
                <TableRow>
                  <TableCell>{cost?.name}</TableCell>
                  <TableCell sx={{textAlign: 'right'}}>
                    {cost?.price_pp ? formatNumber(cost?.price_pp) : ''}
                  </TableCell>
                  <TableCell sx={{textAlign: 'right'}}>
                    {cost?.price ? formatNumber(cost?.price) : ''}
                  </TableCell>
                  <TableCell>
                    {cost?.currency?.name}
                  </TableCell>
                  <TableCell sx={{textAlign: 'right'}}>
                    {cost?.price_in_base_currency ? formatNumber(cost?.price_in_base_currency) : ''}
                  </TableCell>
                </TableRow>
              );
            })}

            <TableRow

              sx={{
                '& .MuiTableCell-root':{
                  fontWeight: 'bold',
                },
                '& .MuiInputBase-root.MuiOutlinedInput-root': {
                  background: '#f9f9f9',
                  boxShadow: 'inset 0px 0px 1px #5b5b5b',

                  '& .MuiInputBase-input':{
                    fontWeight: 'bold',
                  }

                },
              }}
            >
              <TableCell>
                <b>Total Costs</b>
              </TableCell>
              <TableCell sx={{textAlign: 'right'}}>
                {financeData?.total_costs_value_pp?.EUR ? formatPrice(financeData?.total_costs_value_pp?.EUR, true) : ''}
                <br/>
                {financeData?.total_costs_value_pp?.HUF ? formatPrice(financeData?.total_costs_value_pp?.HUF) : ''}
              </TableCell>
              <TableCell sx={{textAlign: 'right'}}>
                {financeData?.total_costs_value?.EUR ? formatPrice(financeData?.total_costs_value?.EUR, true) : ''}
                <br/>
                {financeData?.total_costs_value?.HUF ? formatPrice(financeData?.total_costs_value?.HUF) : ''}
              </TableCell>
              <TableCell></TableCell>
              <TableCell sx={{textAlign: 'right'}}>
                {financeData?.cost?.EUR ? formatPrice(financeData?.cost?.EUR, true) : ''}
                <br/>
                {financeData?.cost?.HUF ? formatPrice(financeData?.cost?.HUF) : ''}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{background: '#f3f3f3'}} colSpan={5}>&nbsp;</TableCell>
            </TableRow>

            <TableRow
              sx={{
                '& .MuiTableCell-root':{
                  fontWeight: 'bold',
                },
                '& .MuiInputBase-root.MuiOutlinedInput-root': {
                  background: '#f9f9f9',
                  boxShadow: 'inset 0px 0px 1px #5b5b5b',

                  '& .MuiInputBase-input':{
                    fontWeight: 'bold',
                  }

                },
              }}
            >
              <TableCell>
                <b>Profit</b>
              </TableCell>
              <TableCell>
              </TableCell>
              <TableCell>
              </TableCell>
              <TableCell>

              </TableCell>
              <TableCell sx={{textAlign: 'right'}}>
                {financeData?.profit?.[financeData?.final_price_currency?.name?financeData?.final_price_currency?.name:'EUR'] ? formatPrice(financeData?.profit?.[financeData?.final_price_currency?.name?financeData?.final_price_currency?.name:'EUR'], true) : ''}
              </TableCell>
            </TableRow>

          </TableBody>
        </Table>
      </TableContainer>

      {openConfirmDialog && (
        <Dialog
          open={openConfirmDialog}
          onClose={() => {
            setOpenConfirmDialog(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {'Are you sure you want to delete it?'}
          </DialogTitle>
          <DialogActions>
            <MUIButton
              onClick={() => {
                setOpenConfirmDialog(false);
              }}
            >
              Cancel
            </MUIButton>
            <MUIButton
              variant={'contained'}
              color={'error'}
              onClick={() => {
                const deletedRow = openConfirmDialog?.row;
                openConfirmDialog?.schema.client
                  .delete(deletedRow.id as number, {id: deletedRow.id})
                  .then((res: any) => {
                    const tempProperties = [...properties];

                    const newRows = tempProperties.filter((contact: any) => {
                      return contact?.id !== deletedRow?.id;
                    });
                    setProperties(newRows);
                    toast.success(
                      `Successfully deleted ${schema.name}: ${
                        deletedRow?.name ? deletedRow?.name : deletedRow?.id
                      }`
                    );
                    setOpenConfirmDialog(false);
                  })
                  .catch((e: any) => {
                    console.log(e);
                    toast.error(
                      `Error: Unable to delete ${schema.name}: ${
                        deletedRow?.name ? deletedRow?.name : deletedRow?.id
                      }`
                    );
                  });
              }}
              autoFocus
            >
              Delete
            </MUIButton>
          </DialogActions>
        </Dialog>
      )}

      {openedSlidingPane?.row?.id && (
        <SlidingPaneEdit
          schema={openedSlidingPane?.schema}
          id={openedSlidingPane?.row?.id}
          onSubmit={() => {
            setOpenedSlidingPane({});
            fetchData();

            toast.success(`Successfully updated ${schema.name}`);
          }}
          propertiesFromTable={
            openedSlidingPane?.row ? openedSlidingPane?.row : {}
          }
          onClose={(value) => {
            setOpenedSlidingPane({});
          }}
        />
      )}
    </>
  );
};
